import {
    Component,
    ContentChild,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { GlobalService } from '../../global';
import { BaseEntityService } from '../../base-entity.service';
import { Column } from '../base-templates/table/table.model';
import { ActionEvent, ActionType, Collection, QueryParams } from '../collection.model';
import { CollectionQuery } from '../collection.query';
import { CollectionService } from '../collection.service';
import { DefaultResponse } from '../../session';
import { resolveDotNotationPath } from '../../../utils/object.utils';
import { catchError } from 'rxjs/operators';
import { ObjectUtils } from "../../../../../../../api/src/_core/utils/utils.object";
import { AdditionalColumn } from "../../../../../../../api/src/_core/models/collection.model";

interface GetEntitiesPayload {
	queryParams: QueryParams;
	append: boolean;
}

@Component({
	selector: 'app-collection-container',
	templateUrl: './collection-container.component.html',
	styleUrls: ['./collection-container.component.scss']
})
export class CollectionContainerComponent implements OnInit, OnChanges, OnDestroy {
	@Input() collection: Collection;
	@Input() scrollableTable: boolean = false;
	@Input() ignoreInitialCall: boolean = false;
	@Input() hideLoadingSpinner: boolean = false;

	@Output() akitaInitialized: EventEmitter<string> = new EventEmitter<string>();
	@Output() eventOutput: EventEmitter<ActionEvent> = new EventEmitter<ActionEvent>();
	@Output() onCreateOutput: EventEmitter<any> = new EventEmitter<any>();
	@Output() onLinkedOutput: EventEmitter<any> = new EventEmitter<any>();

	@ContentChild(TemplateRef) cardTemplate: TemplateRef<any>; // Used to project template to grid and carousel.

	public akitaEntity$: Observable<Collection>;
	public service: BaseEntityService;

	private unsubscribe$: Subject<void> = new Subject<void>();
	private collectionUnsubscribe$: Subject<void> = new Subject<void>(); // For collection-specific subscriptions
	public createAction = { entity: null, action: { id: 'create', name: 'create', icon: '', type: ActionType.CREATE } };

	private getEntitiesTrigger$ = new Subject<GetEntitiesPayload>();

	private getEntities$ = this.getEntitiesTrigger$.pipe(switchMap(({ queryParams, append }) => this.getEntitiesCall(queryParams, append)));

	constructor(
		private readonly globalService: GlobalService,
		public readonly collectionQuery: CollectionQuery,
		private readonly collectionService: CollectionService,
		private readonly router: Router
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		console.log('[DATASOURCE-SWITCH] CollectionContainer: ngOnChanges called', {
			hasCollectionChange: !!changes['collection'],
			isFirstChange: changes['collection']?.firstChange,
			previousCollectionId: changes['collection']?.previousValue?.id,
			currentCollectionId: changes['collection']?.currentValue?.id
		});

		// If collection changes after initialization, reinitialize
		if (changes['collection'] && !changes['collection'].firstChange && this.collection?.id) {
			console.log('[DATASOURCE-SWITCH] CollectionContainer: Collection changed, reinitializing');

			// Clean up old collection subscriptions
			this.collectionUnsubscribe$.next();
			this.collectionUnsubscribe$.complete();
			this.collectionUnsubscribe$ = new Subject<void>();

			// Reinitialize with new collection
			this.initCollection(this.collection);
			this.akitaInitialized.emit(this.collection.id);
		}
	}

	ngOnInit(): void {
		console.log('[DATASOURCE-SWITCH] CollectionContainer: ngOnInit called', {
			collectionId: this.collection?.id
		});
		this.getEntities$.pipe(takeUntil(this.unsubscribe$)).subscribe();
		if (this.collection?.id) {
			this.initCollection(this.collection);
			this.akitaInitialized.emit(this.collection.id);
		} else {
			console.warn('CollectionContainerComponent: No id provided.', this.collection);
		}
	}

	ngOnDestroy() {
		console.log('[DATASOURCE-SWITCH] CollectionContainer: ngOnDestroy called');
		this.collectionUnsubscribe$.next();
		this.collectionUnsubscribe$.complete();
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	/**
       * Initialize the collection variables
	 * @param collection
	 */
	public initCollection(collection: Collection): void {
		console.log('[DATASOURCE-SWITCH] CollectionContainer: initCollection started', {
			collectionId: collection.id,
			methodArgs: collection.methodArgs
		});

		if (collection.service) {
			this.service = this.collection.service;
		} else {
			console.warn('CollectionContainerComponent: Service is not defined.', this.collection);
		}

		// Akita doesn't like to store services or queries, strip out before adding.
		this.collectionService.upsert(this.collection.id, { ...this.collection, service: undefined, query: undefined });

		// Assign akitaEntity to the collection from akita
		this.akitaEntity$ = this.collectionQuery.selectEntity(this.collection.id);

		// Set our initial pagination state
		this.resetPaginationState();

		if (!this.ignoreInitialCall) {
			// Listen for queryParam changes to kick off getting new entries for most collections
			// Use collectionUnsubscribe$ so we can clean up when collection changes
			this.collectionQuery
				.selectEntity(this.collection.id, 'queryParams')
				.pipe(takeUntil(this.collectionUnsubscribe$))
				.subscribe(queryParams => {
					console.log('[DATASOURCE-SWITCH] CollectionContainer: queryParams changed', {
						collectionId: this.collection.id,
						queryParams
					});
					if (this.collectionQuery.getEntity(this.collection.id).getEntitiesOnParamsChange) {
						this.getEntitiesTrigger$.next({
							queryParams,
							append: this.collectionQuery.getEntity(this.collection.id).appendEntities
						});
					}
				});
		}

		console.log('[DATASOURCE-SWITCH] CollectionContainer: initCollection completed', {
			collectionId: collection.id
		});
	}

	/**
	 * Get the entities for the collection
	 * @param queryParams
	 * @param append
	 */
	private getEntitiesCall(queryParams: QueryParams, append: boolean = false): Observable<void> {
		let getMethod = this.collection.getMethod || 'get';

		if (append && this.collectionQuery.getEntity(this.collection.id).cursor) {
			queryParams = { ...queryParams, cursor: this.collectionQuery.getEntity(this.collection.id).cursor };
		}

		console.log('🌐 CollectionContainer making API call:', getMethod, 'with params:', queryParams);
		return this.service?.[getMethod](queryParams, false, true, ...(this.collection.methodArgs || [])).pipe(
			tap((response: DefaultResponse<any>) => {
				console.log('📡 CollectionContainer API response received:', response);
				let results =
					resolveDotNotationPath(this.collection.resultsPath, response) ||
					resolveDotNotationPath('data.results', response) ||
					resolveDotNotationPath('data', response) ||
					[];

				if (append) {
					this.collectionService.addEntities(this.collection.id, results);
				} else {
					console.log('🏗️ Collection setEntities:', this.collection.id, 'Total results:', results.length);
					console.log('🏗️ First result sample:', JSON.stringify(results[0], null, 2));
					this.collectionService.setEntities(this.collection.id, results);
				}

				let cursor = response.data?.cursor;
				if (cursor !== undefined) {
					this.collectionService.update(this.collection.id, { cursor });
				}

				let totalResults =
					resolveDotNotationPath(this.collection.totalResultsPath, response) ||
					resolveDotNotationPath('data.totalResults', response) ||
					resolveDotNotationPath('totalResults', response);

				if (totalResults !== undefined) {
					this.collectionService.update(this.collection.id, { totalResults: totalResults });
				}

				let additionalColumns: AdditionalColumn[] = resolveDotNotationPath(
					this.collection.additionalColumnsPath,
					response
				);

				if (additionalColumns !== undefined) {
					const preparedAdditionalColumns = this.prepareAdditionalColumns(additionalColumns);
					this.collectionService.addAdditionalColumns(this.collection.id, preparedAdditionalColumns);
				}

				if (!this.collectionQuery.getEntity(this.collection.id)?.loaded) {
					this.collectionService.update(this.collection.id, { loaded: true });
				}

				this.updateActiveColumnsFromLocalStorage();
			}),
			catchError(error => {
				this.globalService.triggerErrorMessage(error);
				if (!this.collectionQuery.getEntity(this.collection.id).loaded) {
					this.collectionService.update(this.collection.id, { loaded: true });
				}
				return of();
			})
		);
	}

	public updateQueryParams(params: QueryParams, append?: boolean) {
		const collectionRef = this.collectionQuery.getEntity(this.collection.id);

		// Remove any cursor if we are changing the query params.
		this.collectionService.update(this.collection.id, {
			cursor: undefined,
			appendEntities: append
		});

		this.collectionService.updateQueryParams(this.collection.id, {
			...params,
			page: 1,
			limit: collectionRef.queryParams?.limit || collectionRef.currentTemplate.settings.defaultLimit || 10
		});
	}

	public updateSortState(ev: Sort) {
		this.collectionService.updateQueryParams(this.collection.id, {
			sortBy: { id: ev.active, direction: ev.direction != '' ? ev.direction.toUpperCase() : 'ASC' }
		});
	}

	public updatePaginationState(ev: PageEvent & { append?: boolean }) {
		const paginationState = {
			page: ev.pageIndex + 1,
			limit: ev.pageSize
		};
		this.collectionService.currentPage = paginationState.page;
		this.collectionService.currentLimit = paginationState.limit;

		this.collectionService.update(this.collection.id, {
			cursor: ev.append ? this.collectionQuery.getEntity(this.collection.id).cursor : undefined,
			appendEntities: ev.append
		});


		this.collectionService.updateQueryParams(this.collection.id, paginationState);
	}

	public resetPaginationState(): void {
		const collectionRef = this.collectionQuery.getEntity(this.collection.id);

		// Reset cursor
		this.collectionService.update(this.collection.id, { cursor: undefined });

		const paginationState = {
			page: 1,
			limit: collectionRef.currentTemplate?.settings?.defaultLimit || 10
		};
		this.collectionService.updateQueryParams(this.collection.id, paginationState);
	}

	public setActiveColumns(ids: Column['id'][]) {
		const currentTemplate = this.collectionQuery.getEntity(this.collection.id).currentTemplate
		this.collectionService.setActiveColumns(
			this.collection.id,
			currentTemplate.settings.columns.filter(column => ids.includes(column.id))
		);
		localStorage.setItem(`activeColumnsById-${this.collection.currentTemplate.id}`, JSON.stringify(ids));
	}

	public updateActiveColumnsFromLocalStorage() {
		const columnsInStorage = localStorage.getItem(`activeColumnsById-${this.collection.currentTemplate.id}`);

		if (columnsInStorage) {
			this.setActiveColumns(JSON.parse(columnsInStorage));
		}
	}

	public prepareAdditionalColumns(columns: AdditionalColumn[]): Column[] {
		return columns.map<Column>(column => ({
			id: column.header.text,
			name: `${ObjectUtils.getLocale(column.header.text, column.header.locales)}`,
			path: column.value.path,
			type: 'custom',
			valueFn: entity => {
				const value = resolveDotNotationPath(column.value.path, entity);
				const localeData = resolveDotNotationPath(column.value.localePath, entity);

				return ObjectUtils.getLocale(value, localeData);
			},
			disableSort: true
		}));
	}

	/**
	 * Fires when the template has an action that the user clicks on.
	 * Those actions bubble back to here to be handled.  We need to support most any action that could happen in a template.
	 * @param ev
	 */
	public onActionEvent(ev: ActionEvent) {
		let observable: Observable<any> = of(undefined);

		// Only try to call service method if it exists
		if (ev.action.methodName && this.service && typeof this.service[ev.action.methodName] === 'function') {
			observable = this.service[ev.action.methodName](ev.entity, ev.action.methodParams);
		}

		if (!ev.action.methodParams?.confirm) {
			observable.subscribe(
				() => {
					switch (ev.action.type) {
						case ActionType.REMOVE:
							this.collectionService.removeEntities(this.collection.id, [ev.entity]);
							break;
						case ActionType.GO_TO:
							if (ev.action.methodParams?.url) {
								this.router.navigate([ev.action.methodParams.url, this.collection.id]);
							}
							break;
						case ActionType.SELECT:
							this.collectionService.toggleSelectForEntities(this.collection.id, [ev.entity]);
							break;
						case ActionType.EXPORT:
							if (ev.action.resultMethodName) {
								this.collectionService.exportCollectionEntities(
									this.collection.id,
									[ev.entity],
									this.service,
									ev.action.resultMethodName,
									ev.action.setLoad
								);
							}
							break;
						case ActionType.CLONE:
							if (ev?.entity) {
								this.collectionService.updateQueryParams(this.collection.id, { page: 1 });
							}

						// TODO: Support more action types
					}
				},
				error => this.globalService.triggerErrorMessage(error)
			);
		}

		this.eventOutput.emit(ev);
	}

	onCreate(event: any) {
		this.onCreateOutput.emit(event);
	}

	public toggleSidebar() {
		this.collectionService.toggleSidebar(this.collection.id);
	}

	onLinked(event) {
		this.onLinkedOutput.emit(event);
	}
}
